import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { saveSession, homeForRole } from '../lib/auth';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  // Email passed from Register page via navigation state, or from sessionStorage
  const email = location.state?.email || sessionStorage.getItem('pending_verify_email') || '';
  const userId = location.state?.user_id || sessionStorage.getItem('pending_verify_user_id') || '';

  useEffect(() => {
    if (!userId) {
      navigate('/register');
    }
  }, [userId, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await apiFetch('/api/auth/verify-email', {
        method: 'POST',
        body: { user_id: userId, code: code.trim() },
      });

      if (res.token) {
        sessionStorage.removeItem('pending_verify_email');
        sessionStorage.removeItem('pending_verify_user_id');
        saveSession(res.token, res.user);
        navigate(homeForRole(res.user?.role));
        return;
      }

      setError(res.message || 'Invalid code. Please try again.');
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    try {
      await apiFetch('/api/auth/resend-verify-email', {
        method: 'POST',
        body: { user_id: userId },
      });
      setResent(true);
      setError('');
      setTimeout(() => setResent(false), 6000);
    } catch {
      setError('Could not resend code. Please try again.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <div style={styles.logoMark}>M</div>
          <span style={styles.logoText}>MasterFileAI</span>
        </div>

        <div style={styles.iconWrap}>✉️</div>
        <h1 style={styles.heading}>Verify your email</h1>
        <p style={styles.sub}>
          We sent a 6-digit code to{' '}
          {email ? <strong style={{ color: '#111827' }}>{email}</strong> : 'your email address'}.
          {' '}Enter it below to activate your account.
        </p>

        {error && <div style={styles.errorBox}>{error}</div>}
        {resent && (
          <div style={styles.successBox}>
            ✓ New code sent to {email || 'your email'}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>6-digit code</label>
          <input
            style={styles.codeInput}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            required
            autoFocus
          />

          <button
            type="submit"
            style={{ ...styles.btn, opacity: code.length === 6 ? 1 : 0.5 }}
            disabled={loading || code.length !== 6}
          >
            {loading ? 'Verifying…' : 'Verify & continue →'}
          </button>
        </form>

        <div style={styles.footer}>
          <span style={styles.footerText}>Didn't receive it? </span>
          <button
            onClick={handleResend}
            disabled={resending}
            style={styles.linkBtn}
          >
            {resending ? 'Sending…' : 'Resend code'}
          </button>
        </div>

        <div style={{ marginTop: 8 }}>
          <button onClick={() => navigate('/register')} style={{ ...styles.linkBtn, color: '#6b7280' }}>
            ← Use a different email
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f8f9fb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e2e6ef',
    borderRadius: 16,
    padding: '48px 40px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
    textAlign: 'center',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 28,
  },
  logoMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #6c63ff, #3ecfcf)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 18,
  },
  logoText: {
    color: '#111827',
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: '-0.3px',
  },
  iconWrap: { fontSize: 44, marginBottom: 16 },
  heading: {
    color: '#111827',
    fontSize: 24,
    fontWeight: 700,
    margin: '0 0 12px',
    letterSpacing: '-0.4px',
  },
  sub: {
    color: '#8b8fa8',
    fontSize: 14,
    lineHeight: 1.6,
    margin: '0 0 28px',
  },
  errorBox: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.4)',
    borderRadius: 8,
    color: '#f87171',
    fontSize: 13,
    padding: '10px 14px',
    marginBottom: 16,
    textAlign: 'left',
  },
  successBox: {
    background: 'rgba(16,185,129,0.08)',
    border: '1px solid rgba(16,185,129,0.4)',
    borderRadius: 8,
    color: '#34d399',
    fontSize: 13,
    padding: '10px 14px',
    marginBottom: 16,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    textAlign: 'left',
  },
  label: {
    color: '#9ca3af',
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  codeInput: {
    background: '#f8f9fb',
    border: '1px solid #e2e6ef',
    borderRadius: 10,
    color: '#111827',
    fontSize: 32,
    fontWeight: 700,
    letterSpacing: '0.35em',
    padding: '16px',
    textAlign: 'center',
    width: '100%',
    boxSizing: 'border-box',
  },
  btn: {
    marginTop: 12,
    background: 'linear-gradient(135deg, #6c63ff, #3ecfcf)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    padding: '13px',
    width: '100%',
    transition: 'opacity 0.15s',
  },
  footer: {
    marginTop: 24,
  },
  footerText: {
    color: '#8b8fa8',
    fontSize: 13,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#6c63ff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    padding: 0,
  },
};
